import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

/**
 * Turns the host element into a drag & drop target for files.
 * Emits the dropped files through `filesDropped` -- capped to a single
 * file when `dropZoneMultiple` is false -- and toggles
 * `file-drop-zone--active` while a file drag hovers the host.
 */
@Directive({
    selector: '[ktFileDropZone]'
})
export class FileDropZoneDirective {
    @Input() dropZoneDisabled = false;
    /** When false, a drop of several files only yields the first one. */
    @Input() dropZoneMultiple = true;
    @Output() filesDropped: EventEmitter<File[]> = new EventEmitter();

    @HostBinding('class.file-drop-zone') readonly baseClass = true;
    @HostBinding('class.file-drop-zone--active') isActive = false;

    // dragenter/dragleave also fire for child elements, so keep a depth counter
    // instead of toggling the highlight on every event.
    private depth = 0;

    @HostListener('dragenter', ['$event'])
    onDragEnter(event: DragEvent) {
        if (!this.isFileDrag(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.depth++;
        this.isActive = true;
    }

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent) {
        if (!this.isFileDrag(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        this.isActive = true;
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent) {
        if (!this.isFileDrag(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.depth--;
        if (this.depth <= 0) {
            this.reset();
        }
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent) {
        if (!this.isFileDrag(event)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.reset();

        const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
        if (files.length === 0) {
            return;
        }
        this.filesDropped.emit(this.dropZoneMultiple ? files : files.slice(0, 1));
    }

    private reset() {
        this.depth = 0;
        this.isActive = false;
    }

    private isFileDrag(event: DragEvent): boolean {
        if (this.dropZoneDisabled) {
            return false;
        }
        const types = event.dataTransfer ? event.dataTransfer.types : null;
        if (!types) {
            return false;
        }
        return Array.from(types).indexOf('Files') !== -1;
    }
}
